import { Link, NavLink, useLocation } from "react-router-dom";
import { ShoppingBag, Menu, X, User as UserIcon } from "lucide-react";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useCart } from "@/lib/cart";
import { useAuth } from "@/lib/auth";
import LanguagePicker from "@/components/LanguagePicker";

export default function Navbar() {
  const { count } = useCart();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  const NAV = [
    { to: "/", label: t("nav.home") },
    { to: "/shop", label: t("nav.shop") },
    { to: "/about", label: t("nav.about") },
    { to: "/blog", label: t("nav.journal") },
    { to: "/contact", label: t("nav.contact") },
  ];

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 8);
    h(); window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);

  return (
    <header
      data-testid="main-nav"
      className={`sticky top-0 z-40 backdrop-blur-xl transition-colors ${
        scrolled ? "bg-cream/90 border-b border-line" : "bg-cream/60"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-20">
        <Link to="/" data-testid="logo-link" className="flex flex-col items-center group leading-none">
          <img
            src="/hopsy-logo.webp"
            alt="Hopsy"
            className="h-10 md:h-12 w-auto object-contain transition-transform group-hover:scale-[1.03]"
          />
          <span className="hidden sm:block font-serif italic text-[11px] md:text-[12px] text-sage tracking-wide mt-1 text-center">
            Complete Care For Small Pets
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-sm font-sans tracking-wide transition-colors ${
                  isActive ? "text-forest font-medium" : "text-ink-muted hover:text-forest"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3 md:gap-4">
          <div className="hidden md:block">
            <LanguagePicker />
          </div>
          {user ? (
            <Link
              to="/account"
              data-testid="account-link"
              className="hidden md:flex items-center gap-2 text-sm text-forest hover:text-terra transition-colors"
            >
              {user.picture ? (
                <img src={user.picture} alt="" className="w-7 h-7 rounded-full object-cover" />
              ) : (
                <span className="w-7 h-7 rounded-full bg-forest text-cream text-[11px] flex items-center justify-center font-medium">
                  {(user.name || user.email || "").charAt(0).toUpperCase()}
                </span>
              )}
              <span className="uppercase text-xs tracking-widest">{user.name?.split(" ")[0] || t("nav.myAccount")}</span>
            </Link>
          ) : (
            <Link
              to="/login"
              data-testid="login-link"
              className="hidden md:flex items-center gap-2 text-sm text-forest hover:text-terra transition-colors"
            >
              <UserIcon className="w-5 h-5" strokeWidth={1.5} />
              <span className="uppercase text-xs tracking-widest">{t("nav.signIn")}</span>
            </Link>
          )}
          <Link
            to="/cart"
            data-testid="cart-link"
            className="relative flex items-center gap-2 text-sm text-forest hover:text-terra transition-colors"
          >
            <ShoppingBag className="w-5 h-5" strokeWidth={1.5} />
            <span className="hidden md:inline uppercase text-xs tracking-widest">{t("nav.cart")}</span>
            {count > 0 && (
              <span
                data-testid="cart-count"
                className="absolute -top-2 -right-3 bg-sun text-forest text-[10px] font-sans font-semibold h-5 min-w-5 px-1 flex items-center justify-center"
              >
                {count}
              </span>
            )}
          </Link>
          <button
            data-testid="mobile-menu-btn"
            className="md:hidden text-forest"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-line bg-cream" data-testid="mobile-menu">
          <nav className="flex flex-col px-6 py-6 gap-4">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                className={({ isActive }) =>
                  `text-lg font-serif ${isActive ? "text-forest" : "text-ink-muted"}`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <Link to={user ? "/account" : "/login"} className="text-lg font-serif text-forest flex items-center gap-2">
              <UserIcon className="w-4 h-4" /> {user ? (user.name?.split(" ")[0] || t("nav.myAccount")) : t("nav.signIn")}
            </Link>
            <div className="pt-3 mt-1 border-t border-line">
              <LanguagePicker />
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
